import {
  Badge,
  Button,
  Container,
  CopyButton,
  Divider,
  Group,
  Modal,
  Paper,
  Table,
  TableTdProps,
  TableThProps,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { AppName, BrandImages, Colors } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  generateRandomString,
  getEllipsisFromString,
  getFinancialValueFromNumeric,
} from "../../Lib/Methods";
import { NonPaginatedResponse } from "../../Lib/Responses";
import {
  TicketProps,
  TicketResponseProps,
  TransactionProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Wrapper";
const SingleSupport: React.FC = () => {
  const params = useParams();

  const ticketID = params.ticketID as string;
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `Ticket Details | ${AppName}`;

  const {
    data: data,
    reloadData: reloadTicket,
    isLoading: isLoadingTicket,
  } = usePerformRequest<NonPaginatedResponse<TicketProps>>({
    method: "GET",
    route: Endpoints.GetSingleSupportTicket(ticketID),
  });
  const ticket = data?.data;

  const responseForm = useForm({
    initialValues: {
      message: "",
    },
    validate: {
      message: (value) =>
        value && value.length >= 10 ? null : "At least 10 characters!",
    },
  });
  const [currentReplies, setCurrentReplies] = useState<TicketResponseProps[]>(
    []
  );

  const [isResponding, setResponding] = useState<boolean>(false);
  const SendResponse = async () => {
    if (ticket) {
      const { hasErrors } = responseForm.validate();
      if (!hasErrors) {
        setResponding(true);
        const r = await PerformRequest({
          method: "POST",
          route: Endpoints.GetRespondToTicket(ticketID),
          data: {
            message: responseForm.values.message,
          },
          callback: () => {
            setResponding(false);
            DefaultErrorNotification("Could not send response");
          },
        });
        setResponding(false);
        if (r) {
          GetTicketReplyHistory();
          responseForm.reset();
        }
      }
    }
  };
  const [isGetTicketReplyHistoryLoading, setGetTicketReplyHistoryLoading] =
    useState<boolean>(false);
  const GetTicketReplyHistory = async () => {
    if (ticket) {
      setGetTicketReplyHistoryLoading(true);
      const r = await PerformRequest<
        NonPaginatedResponse<TicketResponseProps[]>
      >({
        method: "GET",
        route: Endpoints.GetTicketReplies(ticket.id),
        callback: () => {
          setGetTicketReplyHistoryLoading(false);
          DefaultErrorNotification("Could not fetch ticket replies");
        },
      });
      setGetTicketReplyHistoryLoading(false);
      if (r && r.data) {
        setCurrentReplies(r.data.data);
      }
    }
  };
  useEffect(() => {
    if (ticket && ticket.id) {
      GetTicketReplyHistory();
    }
  }, [ticket]);

  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);

  const [transactionDetails, setTransactionDetails] =
    useState<TransactionProps>();

  useEffect(() => {
    if (ticket && ticket.id && ticket.transactionID) {
      const fetchTransaction = async () => {
        const r = await PerformRequest<NonPaginatedResponse<TransactionProps>>({
          method: "GET",
          route: Endpoints.GetSingleTransaction(ticket.transactionID as string),
          callback: () => {
            DefaultErrorNotification("Could not fetch transaction");
          },
        });
        if (r && r.data) {
          setTransactionDetails(r.data.data);
        }
      };
      fetchTransaction();
    }
  }, [ticket]);

  const tableHeaderProps: TableThProps = {
    bg: theme === "dark" ? Colors.BlackPrimary : Colors.White,
    c: theme === "dark" ? Colors.White : Colors.BlackPrimary,
  };
  const tableTdProps: TableTdProps = {
    bg: theme === "dark" ? Colors.BlackPrimary : Colors.White,
    c: theme === "dark" ? Colors.White : Colors.BlackPrimary,
  };
  return (
    <Container>
      <div className="single-support-container">
        {isLoadingTicket ? (
          <div className="flex-row justify-center">
            <MegaLoader
              containerStyle={{
                marginTop: 20,
                marginBottom: 20,
              }}
            />
          </div>
        ) : (
          <>
            {ticket && ticket.id ? (
              <>
                {transactionDetails && (
                  <Modal
                    opened={isTransactionModalOpened}
                    onClose={() => {
                      CloseTransactionModal();
                    }}
                    title="Transaction Details"
                    centered
                    size="lg"
                  >
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={90}>ID</Table.Th>
                          <Table.Td {...tableTdProps}>
                            <CopyButton value={transactionDetails.id}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {getEllipsisFromString(
                                      transactionDetails.id,
                                      20
                                    )}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Reference</Table.Th>
                          <Table.Td {...tableTdProps}>
                            <CopyButton value={transactionDetails.reference}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {transactionDetails.reference}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Date</Table.Th>
                          <Table.Td {...tableTdProps}>
                            {dayjs(transactionDetails.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Amount</Table.Th>
                          <Table.Td
                            {...tableTdProps}
                            fw={600}
                            c={Colors.GreenSecondary}
                            fz={17}
                          >
                            $
                            {getFinancialValueFromNumeric(
                              transactionDetails.amount
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Service(s)</Table.Th>
                          <Table.Td {...tableTdProps}>
                            <div className="flex-row align-center temp-service-images">
                              {transactionDetails.services.map((s) => (
                                <img
                                  src={BrandImages.retrieveImage(s, theme)}
                                  alt=""
                                  className="transaction-image-row"
                                />
                              ))}
                            </div>
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Duration</Table.Th>
                          <Table.Td {...tableTdProps}>
                            {transactionDetails.duration}
                          </Table.Td>
                        </Table.Tr>
                      </Table.Thead>
                    </Table>
                    <div className="flex-row align-center">
                      <Button
                        variant="outline"
                        color={Colors.GreenPrimary}
                        onClick={() => {
                          CloseTransactionModal();
                        }}
                        w={120}
                        mt={20}
                      >
                        Close
                      </Button>
                      <Link
                        to={`/d/support/new?transaction=${transactionDetails.id}`}
                      >
                        <Button ml={10} mt={20}>
                          Dispute
                        </Button>
                      </Link>
                    </div>
                  </Modal>
                )}
                <Table variant="vertical" layout="fixed" withTableBorder>
                  <Table.Thead>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th {...tableHeaderProps} w={140}>
                        Category
                      </Table.Th>
                      <Table.Td {...tableTdProps} tt="capitalize">
                        {ticket?.category.replaceAll("-", " ")}
                      </Table.Td>
                    </Table.Tr>
                    {ticket.subscriptionID && (
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th {...tableHeaderProps} w={140}>
                          Subscription
                        </Table.Th>
                        <Table.Td {...tableTdProps} tt="capitalize">
                          <Link
                            to={`/d/subscriptions/${ticket.subscriptionID}`}
                          >
                            <Text c={Colors.GreenPrimary} fz="sm">
                              View
                            </Text>
                          </Link>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {ticket.transactionID && (
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th {...tableHeaderProps} w={140}>
                          Transaction
                        </Table.Th>
                        <Table.Td {...tableTdProps} tt="capitalize">
                          <Text
                            fz="sm"
                            c={Colors.GreenPrimary}
                            className="pointer"
                            onClick={OpenTransactionModal}
                          >
                            View
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th {...tableHeaderProps}>Title</Table.Th>
                      <Table.Td {...tableTdProps}>
                        {getEllipsisFromString(ticket?.title, 120)}
                      </Table.Td>
                    </Table.Tr>

                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th {...tableHeaderProps}>Date Logged</Table.Th>
                      <Table.Td {...tableTdProps}>
                        {dayjs(ticket.createdAt).format("DD MMM YYYY - hh:mmA")}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th {...tableHeaderProps}>Last Updated</Table.Th>
                      <Table.Td
                        {...tableTdProps}
                        c={ticket.updatedAt ? "black" : Colors.RedPrimary}
                      >
                        {ticket.updatedAt ? (
                          <>{dayjs(ticket.updatedAt).format("DD MMM YYYY")}</>
                        ) : (
                          <>Unattended</>
                        )}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th {...tableHeaderProps}>Status</Table.Th>
                      <Table.Td {...tableTdProps}>
                        <Badge
                          size="sm"
                          color={
                            ticket.status === "resolved"
                              ? Colors.GreenPrimary
                              : Colors.OrangeSecondary
                          }
                        >
                          {ticket.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  </Table.Thead>
                </Table>
                <Text
                  my={10}
                  fw={500}
                  c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                >
                  Initial Message
                </Text>
                <Text
                  lh={2.2}
                  c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                >
                  {ticket.message}
                </Text>
                <Divider w="100%" my={10} />
                <Text
                  my={10}
                  fw={500}
                  c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                >
                  Message History
                </Text>

                <div className="history-container">
                  {isGetTicketReplyHistoryLoading && (
                    <MegaLoader
                      containerStyle={{
                        marginTop: 20,
                        marginBottom: 20,
                      }}
                    />
                  )}
                  {currentReplies.length === 0 && (
                    <Text c="dimmed" fz="sm">
                      No replies yet
                    </Text>
                  )}
                  {currentReplies.map((reply) => {
                    return (
                      <Paper
                        mb={10}
                        withBorder
                        radius="md"
                        p={10}
                        style={{
                          borderColor:
                            reply.sender === "user"
                              ? Colors.BluePrimary
                              : Colors.GreenPrimary,
                        }}
                      >
                        <Group>
                          <div>
                            <Text fz="sm">
                              {reply.sender === "user" ? "User" : "Admin"}
                            </Text>
                            <Text fz="xs" c="dimmed">
                              {dayjs(reply.createdAt).format(
                                "DD MMM YYYY - hh:mmA"
                              )}
                            </Text>
                          </div>
                        </Group>
                        <Text fz="sm">{reply.message}</Text>
                      </Paper>
                    );
                  })}
                </div>

                <Divider w="100%" my={10} />
                <Textarea
                  label="Response"
                  placeholder="Enter your response here"
                  spellCheck={false}
                  mb={20}
                  c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                  {...responseForm.getInputProps("message")}
                />
                <div className="flex-row align-center">
                  <Button
                    color={Colors.BluePrimary}
                    mr={10}
                    disabled={!responseForm.isValid()}
                    onClick={SendResponse}
                    loading={isResponding}
                  >
                    Send Response
                  </Button>
                </div>
              </>
            ) : (
              <DisplayAlert
                containerStyle={{
                  marginTop: "20px",
                }}
                title="No Ticket Found"
                children={
                  <div className="flex-col">
                    <Text fz="sm">This ticket does not exist bruh</Text>
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default SingleSupport;
